// Constants
import { DefaultOrderHint } from "@ms/uno-constants/lib/local/OrderHintConstants";
import cloneDeep from "lodash/cloneDeep";
import { CustomFieldChoiceExtensionKeys } from "../../../service/planner/extensions/ICustomFieldChoiceExtensionsResource";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
/**
 * The custom field choice entity implementation.
 */ export class CustomFieldChoice extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed) {
        return {
            displayName: changed.displayName,
            extensions: changed.extensions && this.serializeExtensionsDiff(changed.extensions)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            extensions: (value)=>CustomFieldChoice.deserializeExtensions(value)
        });
    }
    static fromResource(resource) {
        return new CustomFieldChoice({
            id: resource.id,
            version: resource["@etag"],
            displayName: resource.displayName,
            extensions: this.deserializeExtensions(resource.extensions)
        });
    }
    static deserializeExtensions(extensions) {
        const output = {
            orderHint: DefaultOrderHint
        };
        // First parse the order hint data if it exists. Unlike colorData (decorative, safe to lose),
        // a malformed orderHintData payload throws because orderHint is structural — falling back to
        // the default would collapse every corrupted choice to the same position. Matches Bucket.
        const serializedOrderHintData = extensions[CustomFieldChoiceExtensionKeys.orderHintData];
        if (serializedOrderHintData !== undefined && serializedOrderHintData !== null) {
            try {
                const orderHintExtensionData = JSON.parse(serializedOrderHintData);
                if (typeof orderHintExtensionData.orderHint === "string") {
                    output.orderHint = orderHintExtensionData.orderHint;
                }
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=${this.name}][Field=Extensions][Key=orderHintData]`);
            }
        }
        // Read color data from the colorData extension key
        // Value is a stringified JSON object: "{\"backgroundColor\": \"#d8cce7\", \"foregroundColor\": \"#401b6c\"}"
        const serializedColorData = extensions[CustomFieldChoiceExtensionKeys.colorData];
        if (serializedColorData !== undefined) {
            if (serializedColorData === null) {
                output.colorData = null;
            } else {
                try {
                    const colorData = JSON.parse(serializedColorData);
                    if (colorData.backgroundColor !== undefined && colorData.foregroundColor !== undefined) {
                        output.colorData = {
                            backgroundColor: colorData.backgroundColor,
                            foregroundColor: colorData.foregroundColor
                        };
                    }
                } catch  {
                // Ignore malformed colorData extension data
                }
            }
        }
        return output;
    }
    getClone() {
        return new CustomFieldChoice({
            id: this.id,
            version: this.version,
            displayName: this.displayName,
            extensions: cloneDeep(this.extensions)
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            displayName: this.displayName,
            extensions: this.serializeExtensions()
        };
    }
    forCreate() {
        return {
            displayName: this.displayName,
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Sets the choice color and returns a new instance.
     * @param colorData The color data with backgroundColor and foregroundColor, or `null` to clear colors
     * @returns A new ICustomFieldChoice instance with the updated colors
     */ setChoiceColor(colorData) {
        return this.setProperty("extensions", {
            ...this.extensions,
            colorData
        });
    }
    /**
     * Sets the order hint for the choice and returns a new instance.
     * @param orderHint The order hint string value
     * @returns A new ICustomFieldChoice instance with the updated order hint
     */ setOrderHint(orderHint) {
        return this.setProperty("extensions", {
            ...this.extensions,
            orderHint
        });
    }
    /**
     * Serializes only the changed extension fields for use in PATCH requests.
     * @param diff The diff containing only the changed extension properties
     */ serializeExtensionsDiff(diff) {
        const output = {};
        if (diff.colorData !== undefined) {
            if (this.extensions.colorData) {
                const resource = {
                    backgroundColor: this.extensions.colorData.backgroundColor,
                    foregroundColor: this.extensions.colorData.foregroundColor
                };
                output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(resource);
            } else {
                output[CustomFieldChoiceExtensionKeys.colorData] = null;
            }
        }
        if (diff.orderHint !== undefined) {
            const orderHintData = {
                orderHint: this.extensions.orderHint
            };
            output[CustomFieldChoiceExtensionKeys.orderHintData] = JSON.stringify(orderHintData);
        }
        return output;
    }
    serializeExtensions() {
        const output = {};
        if (this.extensions.colorData != null) {
            const colorData = {
                backgroundColor: this.extensions.colorData.backgroundColor,
                foregroundColor: this.extensions.colorData.foregroundColor
            };
            output[CustomFieldChoiceExtensionKeys.colorData] = JSON.stringify(colorData);
        }
        const orderHintData = {
            orderHint: this.extensions.orderHint
        };
        output[CustomFieldChoiceExtensionKeys.orderHintData] = JSON.stringify(orderHintData);
        return output;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.displayName = propertyBag.displayName;
        this.extensions = propertyBag.extensions ?? {
            orderHint: DefaultOrderHint
        };
    }
}
